import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { FlatList, Image, Platform, StyleSheet, View, Text, ImageSourcePropType, Animated, Easing, PixelRatio } from "react-native"
import { ThemeContext } from "../utility/context/ThemeContext"
import { Ionicons } from "@expo/vector-icons"
import { textStyles } from "../style"
import useScreenSize from "./screen-size"

type CarouselItem = {
  image: { src: ImageSourcePropType, alt: string }
  title: string
  body: string
}

const PROGRESS_HEIGHT = 10
const ARROW_ICON_SIZE = 24

const REPEAT_FACTOR = 5
const AUTOPLAY_INTERVAL_MS = 10_000

// Static image maps so Metro/webpack can statically analyze requires
type ScreenSize = '1x' | '2x' | '3x' | '4x'
type SlideImageName = 'SupportMissionDriven' | 'PersonalizeValues' | 'SpendWithPurpose' | 'SavingsImpact'

const IOS_IMAGES: Record<SlideImageName, Record<ScreenSize, ImageSourcePropType>> = {
  SupportMissionDriven: {
    '1x': require('../assets/images/loginAssets/ios/1x/SupportMissionDriven.png'),
    '2x': require('../assets/images/loginAssets/ios/2x/SupportMissionDriven.png'),
    '3x': require('../assets/images/loginAssets/ios/3x/SupportMissionDriven.png'),
    '4x': require('../assets/images/loginAssets/ios/4x/SupportMissionDriven.png'),
  },
  PersonalizeValues: {
    '1x': require('../assets/images/loginAssets/ios/1x/PersonalizeValues.png'),
    '2x': require('../assets/images/loginAssets/ios/2x/PersonalizeValues.png'),
    '3x': require('../assets/images/loginAssets/ios/3x/PersonalizeValues.png'),
    '4x': require('../assets/images/loginAssets/ios/4x/PersonalizeValues.png'),
  },
  SpendWithPurpose: {
    '1x': require('../assets/images/loginAssets/ios/1x/SpendWithPurpose.png'),
    '2x': require('../assets/images/loginAssets/ios/2x/SpendWithPurpose.png'),
    '3x': require('../assets/images/loginAssets/ios/3x/SpendWithPurpose.png'),
    '4x': require('../assets/images/loginAssets/ios/4x/SpendWithPurpose.png'),
  },
  SavingsImpact: {
    '1x': require('../assets/images/loginAssets/ios/1x/SavingsImpact.png'),
    '2x': require('../assets/images/loginAssets/ios/2x/SavingsImpact.png'),
    '3x': require('../assets/images/loginAssets/ios/3x/SavingsImpact.png'),
    '4x': require('../assets/images/loginAssets/ios/4x/SavingsImpact.png'),
  },
}

const ANDROID_IMAGES: Record<SlideImageName, Record<ScreenSize, ImageSourcePropType>> = {
  SupportMissionDriven: {
    '1x': require(
      '../assets/images/loginAssets/android/1x/SupportMissionDriven.png'
    ),
    '2x': require(
      '../assets/images/loginAssets/android/2x/SupportMissionDriven.png'
    ),
    '3x': require(
      '../assets/images/loginAssets/android/3x/SupportMissionDriven.png'
    ),
    '4x': require(
      '../assets/images/loginAssets/android/4x/SupportMissionDriven.png'
    ),
  },
  PersonalizeValues: {
    // sm not present, fall back to md
    '1x': require(
      '../assets/images/loginAssets/android/1x/PersonalizeValues.png'
    ),
    '2x': require(
      '../assets/images/loginAssets/android/2x/PersonalizeValues.png'
    ),
    '3x': require(
      '../assets/images/loginAssets/android/3x/PersonalizeValues.png'
    ),
    '4x': require(
      '../assets/images/loginAssets/android/4x/PersonalizeValues.png'
    ),
  },
  SpendWithPurpose: {
    // sm not present, fall back to md
    '1x': require(
      '../assets/images/loginAssets/android/1x/SpendWithPurpose.png'
    ),
    '2x': require(
      '../assets/images/loginAssets/android/2x/SpendWithPurpose.png'
    ),
    '3x': require(
      '../assets/images/loginAssets/android/3x/SpendWithPurpose.png'
    ),
    '4x': require(
      '../assets/images/loginAssets/android/4x/SpendWithPurpose.png'
    ),
  },
  SavingsImpact: {
    // sm not present, fall back to md
    '1x': require(
      '../assets/images/loginAssets/android/1x/SavingsImpact.png'
    ),
    '2x': require(
      '../assets/images/loginAssets/android/2x/SavingsImpact.png'
    ),
    '3x': require(
      '../assets/images/loginAssets/android/3x/SavingsImpact.png'
    ),
    '4x': require(
      '../assets/images/loginAssets/android/4x/SavingsImpact.png'
    ),
  },
}

function getSlideImage(
  name: SlideImageName,
  platform: string,
  screenSize: ScreenSize,
): ImageSourcePropType {
  if (platform === 'android') {
    return ANDROID_IMAGES[name][screenSize]
  }
  // iOS and web default to the iOS density buckets
  return IOS_IMAGES[name][screenSize]
}

const LoginCarousel = (): React.ReactElement => {
  const flatListRef = useRef<FlatList>(null)
  const autoplayIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const currentIndexRef = useRef<number>(0)
  const theme = useContext(ThemeContext).theme
  const { primary } = theme.colors

  const { width } = useScreenSize()

  // Build a fixed-size looped data set (no unbounded growth)
  const loopedData = useMemo(() => {
    const platform = Platform.OS
    const deviceScale = Platform.OS === 'web'
      ? (typeof window !== 'undefined' ? (window.devicePixelRatio || 1) : 1)
      : PixelRatio.get()
    const physicalWidth = Math.round(width * deviceScale)

    const screenSize = (
      physicalWidth >= 2048 ? '4x' :
      physicalWidth >= 1242 ? '3x' :
      physicalWidth >= 750 ? '2x' :
      '1x'
    ) as ScreenSize

    // Data is intentionally rich so the caller can control copy/images.
    // Replace or override this in the parent if needed.
    const data: CarouselItem[] = [
      {
        image: { src: getSlideImage('SupportMissionDriven', platform, screenSize), alt: "Business with values it supports" },
        title: "Support Mission-Driven Businesses",
        body: [
          "Discover and back enterprises that stand for values that matter to you.",
          "Whether it be who owns it, who it serves, or how it's run.",
        ].join(' '),
      },
      {
        image: { src: getSlideImage('PersonalizeValues', platform, screenSize), alt: "Value selection interface" },
        title: "Personalized to Your Values",
        body: [
          "Enter the issues most important to you (clean energy, social justice, equity, etc.)",
          "and see your spending align.",
        ].join(' '),
      },
      {
        image: { src: getSlideImage('SpendWithPurpose', platform, screenSize), alt: "Transactions screen" },
        title: "Spend with Purpose",
        body: "Track how your everyday spending contributes to causes you care about.",
      },
      {
        image: { src: getSlideImage('SavingsImpact', platform, screenSize), alt: "Business with indicator of local status" },
        title: "Create Positive Impact with Your Savings",
        body: [
          "Your money doesn't have to sit still.",
          "With every dollar you save, you're powering solar energy, strengthening communities,",
          "and helping build a brighter, more sustainable future, all while earning high interest",
          "on your savings at an FDIC regulated bank.",
        ].join(' ')
      }
      // {
      //   image: { src: require('../assets/images/loginAssets/LocalRecommendations.png'),
      // alt: "Business with indicator of local status" },
      //   title: "Local Recommendations That Matter",
      //   body: "Get personalized recommendations for local " +
      //     "businesses that share your values and " +
      //     "explore tens of thousands of purpose-driven businesses nationwide.",
      // },
    ]

    const repeated: CarouselItem[] = []
    for (let i = 0; i < REPEAT_FACTOR; i += 1) {
      repeated.push(...data)
    }
    return repeated
  }, [width])

  const originalLength = loopedData.length / REPEAT_FACTOR
  const middleBlockIndex = Math.floor(REPEAT_FACTOR / 2)
  const INITIAL_INDEX = middleBlockIndex * originalLength

  const [currentIndex, setCurrentIndex] = useState(INITIAL_INDEX)
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 })
  // Track the tallest measured text block so all slides use a uniform height without hard-coding
  const [maxTextHeight, setMaxTextHeight] = useState(0)
  const onWeb = Platform.OS === "web"

  // Animated progress [0..1]
  const progressAnim = useRef(new Animated.Value(0)).current

  const normalizedIndex = useMemo(() => {
    const n = ((currentIndex % originalLength) + originalLength) % originalLength
    return n
  }, [currentIndex, originalLength])

  // Derived percent kept for potential consumers; not used directly after animation switch

  // Animate progress when slide changes
  useEffect(() => {
    const target = (normalizedIndex + 1) / Math.max(originalLength, 1)
    Animated.timing(progressAnim, {
      toValue: target,
      duration: 350,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: false, // animating width
    }).start()
  }, [normalizedIndex, originalLength, progressAnim])

  const recenterIfNeeded = useCallback((index: number) => {
    // Keep the list around the middle block to prevent hitting ends
    const totalLength = loopedData.length
    const lowerBound = originalLength
    const upperBound = totalLength - originalLength - 1

    if (index <= lowerBound || index >= upperBound) {
      const normalized = ((index % originalLength) + originalLength) % originalLength
      const target = INITIAL_INDEX + normalized
      try {
        flatListRef.current?.scrollToIndex({ index: target, animated: false })
      } catch {
        // Fallback in case getItemLayout hasn't measured yet
        flatListRef.current?.scrollToOffset({ offset: dimensions.width * target, animated: false })
      }
      currentIndexRef.current = target
      setCurrentIndex(target)
      return true
    }
    return false
  }, [dimensions.width, loopedData.length, originalLength, INITIAL_INDEX])

  const startAutoplay = useCallback(() => {
    if (autoplayIntervalRef.current) clearInterval(autoplayIntervalRef.current)

    autoplayIntervalRef.current = setInterval(() => {
      const nextIndex = currentIndexRef.current + 1
      // On web, programmatic scrolls may not emit momentum events.
      // Explicitly update state and ref so bubbles advance.
      currentIndexRef.current = nextIndex
      setCurrentIndex(nextIndex)
      try {
        flatListRef.current?.scrollToIndex({ index: nextIndex, animated: true })
      } catch {
        flatListRef.current?.scrollToOffset({
          offset: dimensions.width * nextIndex,
          animated: true,
        })
      }
      // Keep indices near the middle to avoid hitting ends
      recenterIfNeeded(nextIndex)
    }, AUTOPLAY_INTERVAL_MS)
  }, [dimensions.width, recenterIfNeeded])

  const handleScrollEnd = (e) => {
    const index = Math.round(e.nativeEvent.contentOffset.x / dimensions.width)
    currentIndexRef.current = index
    setCurrentIndex(index)
    const recentred = recenterIfNeeded(index)
    if (!recentred) {
      // ensure autoplay keeps running
      startAutoplay()
    }
  }

  // On web, wheel/drag scrolling may not trigger momentum events reliably.
  // Track index during scroll so bubble indicators stay in sync.
  const handleScroll = useCallback((e) => {
    if (!dimensions.width) return
    const x = e.nativeEvent?.contentOffset?.x || 0
    const idx = Math.round(x / dimensions.width)
    if (idx !== currentIndexRef.current) {
      currentIndexRef.current = idx
      setCurrentIndex(idx)
    }
  }, [dimensions.width])

  useEffect(() => {
    if (dimensions.width > 0) {
      // Position at initial index on first layout
      try {
        flatListRef.current?.scrollToIndex({ index: INITIAL_INDEX, animated: false })
      } catch {
        flatListRef.current?.scrollToOffset({
          offset: dimensions.width * INITIAL_INDEX,
          animated: false,
        })
      }
      currentIndexRef.current = INITIAL_INDEX
      setCurrentIndex(INITIAL_INDEX)
      startAutoplay()
    }
    return () => {
      if (autoplayIntervalRef.current) clearInterval(autoplayIntervalRef.current)
    }
  }, [dimensions.width, INITIAL_INDEX, startAutoplay])

  // When width changes, reset measured text height
  // This ensures layout reflows across devices/orientations
  useEffect(() => {
    setMaxTextHeight(0)
  }, [dimensions.width])

  const handleButtonPress = (direction: "back" | "forward") => {
    let newIndex
    if (direction === "back") {
      newIndex = currentIndex - 1
    } else {
      newIndex = currentIndex + 1
    }
    try {
      flatListRef.current?.scrollToIndex({ index: newIndex, animated: true })
    } catch {
      flatListRef.current?.scrollToOffset({ offset: dimensions.width * newIndex, animated: true })
    }
    currentIndexRef.current = newIndex
    setCurrentIndex(newIndex)
    // Recentre if we navigated close to edges
    recenterIfNeeded(newIndex)
    startAutoplay()

  }

  return (
    <View
      style={styles.container}
      onLayout={(e) => {
        const { width, height } = e.nativeEvent.layout
        setDimensions({ width, height })
      }}
    >
      {/* Top progress bar */}
      <View style={styles.progressContainer}>
        {
          onWeb ? (
            <Ionicons
              name="chevron-back"
              size={ARROW_ICON_SIZE}
              color={primary}
              onPress={() => handleButtonPress("back")}
            />
          ) : null
        }
        <ProgressBar
          progress={progressAnim}
          color={primary}
          trackColor={theme.colors.bgSecond}
        />
        {
          onWeb ? (
            <Ionicons
              name="chevron-forward"
              size={ARROW_ICON_SIZE}
              color={primary}
              onPress={() => handleButtonPress("forward")}
            />
          ) : null
        }
      </View>
      {dimensions.width > 0 && (
        <FlatList
          ref={flatListRef}
          horizontal
          pagingEnabled
          showsHorizontalScrollIndicator={false}
          onScroll={onWeb ? handleScroll : undefined}
          scrollEventThrottle={16}
          onMomentumScrollEnd={handleScrollEnd}
          data={loopedData}
          keyExtractor={(_, index) => index.toString()}
          contentContainerStyle={{ alignItems: "center", maxHeight: "100%" }}
          renderItem={({ item, index }: { item: CarouselItem, index: number }) => {
            const sliceCount = originalLength
            const sliceIndex = ((index % originalLength) + originalLength) % originalLength
            return (
              <View
                style={{
                  height: "100%",
                  width: dimensions.width,
                  justifyContent: "flex-end",
                }}
              >
                {/* Background image sliced into thirds (or number of slides) */}
                <BackgroundSlice
                  width={dimensions.width}
                  height={dimensions.height}
                  index={sliceIndex}
                  count={sliceCount}
                />
                {/* Foreground overlay and slide illustration */}
                <View
                  style={[
                    StyleSheet.absoluteFillObject,
                    {
                      backgroundColor: `${theme.colors.bgAlt}40`,
                      alignItems: "center",
                      justifyContent: "space-between",
                      paddingVertical: 16,
                      gap: 16
                    },
                  ]}
                >
                  <Image
                    source={
                      theme.name === 'dark' ?
                      require('../assets/images/logo/StackedLogoWhite.png')
                      : require('../assets/images/logo/StackedLogoBlack.png')
                    }
                    style={{height: '10%', minHeight: 50, maxHeight: 75, aspectRatio: 1}}
                    resizeMode="contain"
                    resizeMethod="scale"
                  />
                  <Image
                    source={item.image.src}
                    accessibilityLabel={item.image.alt}
                    style={{ flex: 1, width: '100%' }}
                    resizeMode="contain"
                  />
                </View>
                <View
                  style={{
                    // Uniform height across slides based on tallest measured content
                    height: maxTextHeight > 0 ? maxTextHeight + 16 + 16 : undefined,
                    paddingHorizontal: 24,
                    paddingTop: 16,
                    paddingBottom: 16,
                    backgroundColor: theme.colors.bgMain,
                  }}
                >
                  <View
                    onLayout={(e) => {
                      const h = Math.ceil(e.nativeEvent.layout.height)
                      if (h > maxTextHeight) setMaxTextHeight(h)
                    }}
                  >
                    <Text
                      style={[textStyles.h2, { color: theme.colors.textMain }]}
                    >
                      {item.title}
                    </Text>
                    <Text style={[styles.body, { color: theme.colors.textSecond }]}>
                      {item.body}
                    </Text>
                  </View>
                </View>
              </View>
            )
          }}
          getItemLayout={(_, index) => ({
            length: dimensions.width,
            offset: dimensions.width * index,
            index,
          })}
          initialScrollIndex={INITIAL_INDEX}
          onScrollToIndexFailed={(info) => {
            setTimeout(() => {
              flatListRef.current?.scrollToOffset({
                offset: info.averageItemLength * info.index,
                animated: false,
              })
            }, 0)
          }}
          removeClippedSubviews
          windowSize={3}
          initialNumToRender={loopedData.length}
        />
      )}
      
    </View>
  )
}

// Animated progress bar component (local to this file)
const ProgressBar = ({
  progress,
  color,
  trackColor,
}: {
  progress: Animated.Value
  color: string
  trackColor: string
}) => {
  const [trackWidth, setTrackWidth] = useState(0)

  return (
    <View
      style={[styles.progressTrack, { backgroundColor: trackColor }]}
      onLayout={(e) => setTrackWidth(e.nativeEvent.layout.width)}
    >
      <Animated.View
        style={[
          styles.progressFill,
          {
            width: Animated.multiply(progress, trackWidth),
            backgroundColor: color,
          },
        ]}
      />
    </View>
  )
}


const styles = StyleSheet.create({
  container: {
    flex: 1,
    maxHeight: "100%",
    gap: 16,
  },
  progressContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  progressTrack: {
    flex: 1,
    height: PROGRESS_HEIGHT,
    opacity: 1,
    borderRadius: 10,
    marginHorizontal: 16,
    overflow: 'hidden',
  },
  progressFill: {
    height: PROGRESS_HEIGHT,
  },
  title: {
    fontSize: 22,
    fontWeight: '700',
    marginTop: 16,
  },
  body: {
    fontSize: 14,
    lineHeight: 20,
    marginTop: 8,
  },
  arrowContainer: {
    position: "absolute",
    flexDirection: "row",
    alignSelf: "center",
    alignItems: "center",
    justifyContent: 'space-between',
    width: 70,
  },
})

export default LoginCarousel

// Background image slice renderer
const BackgroundSlice = ({
  width,
  height,
  index,
  count,
}: { width: number, height: number, index: number, count: number }) => {
  // Guard: avoid invalid sizes
  if (!width || !height || count <= 0) return null as unknown as React.ReactElement

  const totalWidth = width * count
  const translateX = -width * index

  return (
    <View style={[StyleSheet.absoluteFillObject, { overflow: 'hidden' }]}> 
      <Image
        // Using a single background image and shifting it so only the desired slice shows
        source={require('../assets/images/loginAssets/background.png')}
        style={{ width: totalWidth, height, transform: [{ translateX }] }}
        resizeMode="cover"
        resizeMethod="scale"
      />
    </View>
  )
}